import cx from 'classnames'
import { motion, useInView, useScroll, useTransform } from 'framer-motion'
import React, { useRef } from 'react'
import * as styles from '../../styles/components/content-blocks/trusted-by.module.scss'
import { bezierEaseIn } from '../../utils/styles'
import ContentWrapper from '../content-wrapper'
import SmallTitle from '../small-title'
import TypeWriter from '../typewriter'
import { ContentBlockBase } from './shared'
import { graphql, useStaticQuery } from 'gatsby'
import { PartnerBlok, TrustedByBlok } from '../../utils/storyblok'

export interface TrustedByBlockProps extends ContentBlockBase {
    cmsId: string
}

const Partner = ({ blok }: {blok: PartnerBlok}) => {
    if (!blok) {
        return null
    }
    return (
        <div className={styles.partner}>
            <a href={blok.url} target="_blank" rel="noopener noreferrer">
                <img
                    className={styles.partnerLogo}
                    src={`${blok.logo?.filename}/m/0x100`}
                    alt={blok.name}
                />
            </a>
        </div>
    )
}

const TrustedByBlock = (props: TrustedByBlockProps) => {
    const { cmsId, blockId } = props

    const blok = useTrustedByData(cmsId)
    if (!blok) {
        return null
    }

    const targetRef = useRef<HTMLDivElement>(null)
    const { scrollYProgress } = useScroll({ target: targetRef, offset: ['start end', 'start start'] })
    const opacity = useTransform(scrollYProgress, [0.1, 0.7], [0, 1], { ease: bezierEaseIn })
    const hasBeenInView = useInView(targetRef, { margin: '-50%', once: true })

    console.log('trusted by block', blok)

    return (
        <>
            <ContentWrapper className={cx(styles.container)}>
                <div
                    id={blockId}
                    ref={targetRef}
                    className={cx(styles.contentContainer)}
                >
                    {blok.title &&
                        <SmallTitle className={cx(styles.title)} aria-label={blok.title}>
                            <TypeWriter text={blok.title} enabled={hasBeenInView} />
                        </SmallTitle>
                    }
                    <motion.div className={styles.partnersContainer} style={{ opacity }}>
                        {blok.partnersList?.map((partner, idx) =>
                            <Partner key={`partner-${idx}`} blok={partner} />,
                        )}
                    </motion.div>
                </div>
            </ContentWrapper>
        </>
    )
}

export default TrustedByBlock

const useTrustedByData = (id: string): TrustedByBlok | null => {
    const res = useStaticQuery(graphql`
        query {
            trustedBy: allStoryblokEntry(
                filter: {field_component: {eq: "trusted-by"}}
            ) {
                nodes {
                    slug
                    blokStr: content
                }
            }
            partners: allStoryblokEntry(
                filter: {field_component: {eq: "partner"}}
            ) {
                nodes {
                    uuid
                    blokStr: content
                }
            }    
        }
    `)
    const trustedByNodes = res.trustedBy?.nodes ?? []
    const partnerNodes = res.partners?.nodes ?? []

    const { blokStr: trustedByBlockStr } = trustedByNodes.filter((node: any) => node?.slug === id)[0] ?? {}
    if (!trustedByBlockStr) {
        return null
    }

    const blok = JSON.parse(trustedByBlockStr) as TrustedByBlok
    blok.partnersList = []
    blok.partners.forEach((partnerUid: string) => {
        const partnerNode = partnerNodes.find((node: any) => node.uuid === partnerUid)
        if (partnerNode) {
            blok.partnersList?.push(JSON.parse(partnerNode.blokStr) as PartnerBlok)
        }
    })

    return blok
}
